<template>
  <div class="tg-page-root min-h-screen bg-white dark:bg-gray-950">
    <!-- Header Section：標題靠左、元素垂直對齊 -->
    <div
      class="mobile-frame-fixed-top fixed top-0 z-30 bg-white/95 dark:bg-gray-900/95 backdrop-blur-md border-b border-gray-100 dark:border-gray-800 shadow-sm"
    >
      <div class="flex items-center gap-3 px-4 py-4">
        <button
          type="button"
          aria-label="back"
          @click="goBack"
          class="flex items-center justify-center w-10 h-10 rounded-xl text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-800 active:scale-95 transition-all flex-shrink-0"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 class="text-lg font-bold text-gray-800 dark:text-gray-100 flex-1 text-left truncate">{{ $t('PageTitle_Address_Add') }}</h1>
        <div class="w-10 flex-shrink-0"></div>
      </div>
    </div>
    <div class="fixed-header-spacer" style="--fixed-header-spacer-height: 72px; height: 72px;"></div>

    <div class="px-4 py-6">
      <div class="space-y-4">
        <div class="address-field">
          <div class="field-label">{{ $t('Address_Region_Label') }}</div>
          <input
            v-model="region"
            type="text"
            class="field-input"
            :placeholder="$t('Address_Region_Placeholder')"
          />
          <div class="field-icon">▾</div>
        </div>

        <div class="address-field">
          <div class="field-label">{{ $t('Address_Receiver_Label') }}</div>
          <input
            v-model="receiverName"
            type="text"
            class="field-input"
            :placeholder="$t('Address_Receiver_Placeholder')"
          />
        </div>

        <div class="address-field">
          <div class="field-label">{{ $t('Address_Phone_Label') }}</div>
          <button type="button" class="field-prefix" @click="noop">
            {{ dialCode }}
            <span class="ml-1">▾</span>
          </button>
          <input
            v-model="phone"
            type="tel"
            class="field-input"
            :placeholder="$t('Address_Phone_Placeholder')"
          />
        </div>

        <div class="address-field items-start">
          <div class="field-label pt-1">{{ $t('Address_Detail_Label') }}</div>
          <textarea
            v-model="detailAddress"
            rows="3"
            class="field-input resize-none"
            :placeholder="$t('Address_Detail_Placeholder')"
          ></textarea>
        </div>
      </div>

      <button
        class="w-full mt-8 bg-brand text-white py-3.5 rounded-xl font-bold text-lg shadow-lg hover:bg-brand/90 active:scale-[0.98] transition-all duration-300"
        type="button"
        @click="confirmAddress"
      >
        {{ $t('Address_Confirm') }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { showAppNotification } from '@/utils/appNotification'
import { getStorage, setStorage } from '@/utils/storage'
import { STORAGE_KEYS } from '@/constants/storageKeys'

const router = useRouter()
const { t } = useI18n()

// 使用 composable
const region = ref('')
const receiverName = ref('')
const dialCode = ref('+86')
const phone = ref('')
const detailAddress = ref('')

const goBack = () => {
  router.back()
}

const confirmAddress = () => {
  if (!region.value) {
    showAppNotification({
      message: t('Address_Validation_Region'),
      type: 'warning',
      duration: 2500,
      position: 'bottom-right'
    })
    return
  }
  if (!receiverName.value.trim()) {
    showAppNotification({
      message: t('Address_Validation_Receiver'),
      type: 'warning',
      duration: 2500,
      position: 'bottom-right'
    })
    return
  }
  if (!phone.value.trim()) {
    showAppNotification({
      message: t('Address_Validation_Phone'),
      type: 'warning',
      duration: 2500,
      position: 'bottom-right'
    })
    return
  }
  if (!detailAddress.value.trim()) {
    showAppNotification({
      message: t('Address_Validation_Detail'),
      type: 'warning',
      duration: 2500,
      position: 'bottom-right'
    })
    return
  }

  const list = loadAddressList()
  const newAddress = {
    id: Date.now(),
    name: receiverName.value.trim(),
    phone: `${dialCode.value} ${phone.value.trim()}`,
    detail: `${region.value}${region.value ? ', ' : ''}${detailAddress.value.trim()}`,
    isDefault: list.length === 0 || !list.some((item) => item.isDefault)
  }

  const nextList = [...list, newAddress]
  setStorage(STORAGE_KEYS.ADDRESS_LIST, nextList)
  setStorage(STORAGE_KEYS.SELECTED_ADDRESS, newAddress)
  router.back()
}

const loadAddressList = () => {
  return getStorage(STORAGE_KEYS.ADDRESS_LIST, [])
}

const noop = () => {}
</script>

<style scoped>
.address-field {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 12px 14px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.field-label {
  color: #374151;
  min-width: 72px;
  font-size: 14px;
  font-weight: 600;
}

.field-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: #374151;
}

.field-input::placeholder {
  color: #9ca3af;
}

.field-prefix {
  border: none;
  background: transparent;
  font-size: 14px;
  color: #374151;
  display: inline-flex;
  align-items: center;
}

html.dark .address-field {
  background: #1f2937;
  border-color: #374151;
}

html.dark .field-label,
html.dark .field-input,
html.dark .field-prefix {
  color: #e5e7eb;
}

html.dark .field-input::placeholder {
  color: #6b7280;
}

.field-icon {
  color: #9ca3af;
  font-size: 16px;
}
</style>
